import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { BodyPortal } from '../body-portal';

interface CalendarDay {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
}

type PickerMode = 'days' | 'years';

const DROPDOWN_WIDTH = 280;
const DROPDOWN_GAP = 6;
const DROPDOWN_ESTIMATED_HEIGHT = 320;
const VIEWPORT_MARGIN = 8;

const MONTH_NAMES = [
  'Leden',
  'Únor',
  'Březen',
  'Duben',
  'Květen',
  'Červen',
  'Červenec',
  'Srpen',
  'Září',
  'Říjen',
  'Listopad',
  'Prosinec',
];

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

@Component({
  selector: 'tc-date-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.css',
})
export class DatePickerComponent implements OnInit, OnDestroy {
  private readonly elRef = inject(ElementRef);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly vcr = inject(ViewContainerRef);
  private readonly portal = new BodyPortal(this.vcr, this.renderer, this.document);
  private readonly onScrollCapture = () => {
    if (this.open()) this.updatePosition();
  };

  @ViewChild('dropdownTpl', { read: TemplateRef })
  private dropdownTpl!: TemplateRef<unknown>;

  readonly dayLabels = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

  value = input<string>('');
  ariaLabel = input<string>('Výběr data');
  valueChange = output<string>();

  readonly open = signal(false);
  readonly mode = signal<PickerMode>('days');
  readonly dropdownTop = signal<number | null>(null);
  readonly dropdownBottom = signal<number | null>(null);
  readonly dropdownLeft = signal<number | null>(null);
  private readonly _selected = signal<Date | null>(null);
  private readonly _viewYear = signal(new Date().getFullYear());
  private readonly _viewMonth = signal(new Date().getMonth());
  private readonly _yearsBase = signal(new Date().getFullYear());

  constructor() {
    effect(() => {
      const v = this.value();
      if (v) {
        const [y, m, d] = v.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        untracked(() => {
          this._selected.set(date);
          this._viewYear.set(y);
          this._viewMonth.set(m - 1);
          this._yearsBase.set(y);
        });
      } else {
        untracked(() => this._selected.set(null));
      }
    });
  }

  readonly displayDate = computed(() => {
    const d = this._selected();
    if (!d) return '— nevybráno —';
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  });

  readonly viewMonthLabel = computed(
    () => `${MONTH_NAMES[this._viewMonth()]} ${this._viewYear()}`,
  );

  readonly yearsLabel = computed(() => {
    const start = this.yearsStart();
    return `${start}–${start + 11}`;
  });

  readonly yearsGrid = computed(() => {
    const start = this.yearsStart();
    return Array.from({ length: 12 }, (_, i) => start + i);
  });

  readonly calendarDays = computed((): CalendarDay[] => {
    const today = new Date();
    const year = this._viewYear();
    const month = this._viewMonth();
    const firstDay = new Date(year, month, 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const days: CalendarDay[] = [];
    const startDate = new Date(year, month, 1 - startOffset);
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push({
        date,
        inMonth: date.getMonth() === month,
        isToday: isSameDay(date, today),
      });
    }
    return days;
  });

  private yearsStart(): number {
    const base = this._yearsBase();
    return base - (base % 12);
  }

  isViewYear(y: number): boolean {
    return this._viewYear() === y;
  }

  isSel(date: Date): boolean {
    const sel = this._selected();
    return sel !== null && isSameDay(date, sel);
  }

  dayAria(date: Date): string {
    return `${date.getDate()}. ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent) {
    if (
      this.open() &&
      !this.elRef.nativeElement.contains(e.target) &&
      !this.portal.contains(e.target)
    ) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEsc() {
    this.close();
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.open()) this.updatePosition();
  }

  ngOnInit(): void {
    this.document.addEventListener('scroll', this.onScrollCapture, true);
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('scroll', this.onScrollCapture, true);
    this.portal.detach();
  }

  toggle() {
    if (!this.dropdownTpl) {
      return;
    }

    if (this.open()) {
      this.close();
      return;
    }
    this.open.set(true);
    this.portal.attach(this.dropdownTpl);
    this.updatePosition();
  }

  private close() {
    this.open.set(false);
    this.mode.set('days');
    this.portal.detach();
  }

  private updatePosition(): void {
    const trigger = this.elRef.nativeElement.querySelector(
      '.dp-trigger',
    ) as HTMLElement | null;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward =
      spaceBelow < DROPDOWN_ESTIMATED_HEIGHT && spaceAbove > spaceBelow;

    if (openUpward) {
      this.dropdownTop.set(null);
      this.dropdownBottom.set(
        window.innerHeight - rect.top + DROPDOWN_GAP,
      );
    } else {
      this.dropdownBottom.set(null);
      this.dropdownTop.set(rect.bottom + DROPDOWN_GAP);
    }

    let left = rect.left;
    if (left + DROPDOWN_WIDTH > window.innerWidth - VIEWPORT_MARGIN) {
      left = window.innerWidth - DROPDOWN_WIDTH - VIEWPORT_MARGIN;
    }
    if (left < VIEWPORT_MARGIN) {
      left = VIEWPORT_MARGIN;
    }
    this.dropdownLeft.set(left);
  }

  prevMonth() {
    if (this._viewMonth() === 0) {
      this._viewMonth.set(11);
      this._viewYear.update((y) => y - 1);
    } else {
      this._viewMonth.update((m) => m - 1);
    }
    this._yearsBase.set(this._viewYear());
  }

  nextMonth() {
    if (this._viewMonth() === 11) {
      this._viewMonth.set(0);
      this._viewYear.update((y) => y + 1);
    } else {
      this._viewMonth.update((m) => m + 1);
    }
    this._yearsBase.set(this._viewYear());
  }

  toggleYears() {
    this.mode.update((m) => (m === 'days' ? 'years' : 'days'));
    if (this.mode() === 'years') this._yearsBase.set(this._viewYear());
    if (this.open()) this.updatePosition();
  }

  prevYears() {
    this._yearsBase.update((y) => y - 12);
  }

  nextYears() {
    this._yearsBase.update((y) => y + 12);
  }

  selectYear(y: number) {
    this._viewYear.set(y);
    this._yearsBase.set(y);
    this.mode.set('days');
  }

  selectDay(date: Date) {
    this._selected.set(date);
    this._viewYear.set(date.getFullYear());
    this._viewMonth.set(date.getMonth());
    this._yearsBase.set(date.getFullYear());
    this.close();
    this.valueChange.emit(toIso(date));
  }

  goToday() {
    this.selectDay(new Date());
  }
}

